import { useRef, useCallback } from 'react'
import { Box } from '@mui/material'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import SectionCarouselHeader, { SectionCarouselFooterNav } from '../common/SectionCarouselHeader'
import HomeTestimonialCard from './HomeTestimonialCard'
import { homeTestimonials } from '../../data/homeContent'
import {
  HOME_MOBILE_MQ,
  homeMobilePaddingX,
  homeSectionPy,
  homeSectionTitleSx,
} from './homeMobileLayout'
import { SLIDER_TRANSITION_SPEED } from '../../theme/slider'
import 'swiper/css'

const SLIDE_WIDTH = 440
const SLIDE_GAP = 40
const CARD_SHADOW_BLEED = 8
const MOBILE_SLIDE_GAP = 20

export default function ExperiencesThatInspireSection() {
  const swiperRef = useRef(null)
  const slides = [...homeTestimonials, ...homeTestimonials]

  const handleSwiper = useCallback((swiper) => {
    swiperRef.current = swiper
    requestAnimationFrame(() => swiper.autoplay?.start())
  }, [])

  const slidePrev = useCallback(() => {
    swiperRef.current?.slidePrev()
  }, [])

  const slideNext = useCallback(() => {
    swiperRef.current?.slideNext()
  }, [])

  return (
    <Box
      component="section"
      sx={{
        width: '100%',
        maxWidth: 1920,
        mx: 'auto',
        pl: homeMobilePaddingX,
        pr: 0,
        pt: homeSectionPy,
        pb: { xs: '80px', sm: 10, lg: '150px' },
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '32px', md: '80px' },
          [HOME_MOBILE_MQ]: { gap: '32px' },
        }}
      >
        <SectionCarouselHeader
          title="Experiences That Inspire"
          titleSx={{
            ...homeSectionTitleSx,
            maxWidth: 1520,
            pr: homeMobilePaddingX,
            boxSizing: 'border-box',
            mb: 0,
            [HOME_MOBILE_MQ]: { fontSize: 28 },
          }}
          onPrev={slidePrev}
          onNext={slideNext}
          headerSx={{
            mb: 0,
            maxWidth: 1520,
            width: '100%',
            pr: homeMobilePaddingX,
            boxSizing: 'border-box',
          }}
        />

        <Box
          sx={{
            width: {
              xs: 'calc(100vw - 40px)',
              sm: 'calc(100vw - 48px)',
              md: 'calc(100vw - 80px)',
              lg: 'calc(100vw - 160px)',
              xl: 'calc(100vw - 400px)',
            },
            maxWidth: 'none',
            overflow: 'visible',
            position: 'relative',
            '& .swiper': { overflow: 'visible', width: '100%' },
            '& .swiper-wrapper': { alignItems: 'stretch' },
            '& .swiper-slide': {
              width: {
                xs: 'calc(100vw - 40px) !important',
                sm: '340px !important',
                md: '400px !important',
                lg: `${SLIDE_WIDTH + CARD_SHADOW_BLEED * 2}px !important`,
              },
              height: 'auto',
              flexShrink: 0,
              boxSizing: 'border-box',
              padding: `${CARD_SHADOW_BLEED}px`,
              overflow: 'visible',
              [HOME_MOBILE_MQ]: {
                width: 'calc(100vw - 40px) !important',
              },
            },
          }}
        >
          <Swiper
            modules={[Autoplay]}
            onSwiper={handleSwiper}
            slidesPerView="auto"
            spaceBetween={MOBILE_SLIDE_GAP}
            loop
            loopAdditionalSlides={homeTestimonials.length}
            loopPreventsSliding={false}
            observer
            observeParents
            resizeObserver
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
              waitForTransition: true,
            }}
            speed={SLIDER_TRANSITION_SPEED}
            grabCursor
            breakpoints={{
              426: { spaceBetween: 24 },
              900: { spaceBetween: 32 },
              1200: { spaceBetween: SLIDE_GAP },
            }}
          >
            {slides.map((item, index) => (
              <SwiperSlide key={`${item.name}-${index}`}>
                <HomeTestimonialCard {...item} />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>

        <SectionCarouselFooterNav
          onPrev={slidePrev}
          onNext={slideNext}
          sx={{ pr: homeMobilePaddingX }}
        />
      </Box>
    </Box>
  )
}
